import {
  Accessor,
  createContext,
  createResource,
  ParentComponent,
  Resource,
  useContext,
} from "solid-js";

import type { APIRouter } from "~/trpc";
import { useAuth } from "./AuthProvider";

import { createTRPCClient, TRPCClient } from "@trpc/client";

import type {
  AnyRouter,
  inferProcedureInput,
  inferProcedureOutput,
  Router,
} from "@trpc/server";

type inferQueryProcedures<R extends AnyRouter> = R extends Router<
  any,
  any,
  infer Q,
  any,
  any,
  any
>
  ? Q
  : never;
type inferMutationProcedures<R extends AnyRouter> = R extends Router<
  any,
  any,
  any,
  infer M,
  any,
  any
>
  ? M
  : never;

export type Queries = inferQueryProcedures<APIRouter>;
export type Mutations = inferMutationProcedures<APIRouter>;
export type inferInput<K extends keyof Mutations | keyof Queries> =
  K extends keyof Mutations
    ? inferProcedureInput<Mutations[K]>
    : K extends keyof Queries
    ? inferProcedureInput<Queries[K]>
    : never;
export type inferOutput<K extends keyof Mutations | keyof Queries> =
  K extends keyof Mutations
    ? inferProcedureOutput<Mutations[K]>
    : K extends keyof Queries
    ? inferProcedureOutput<Queries[K]>
    : never;

const rpcContext = createContext<TRPCClient<APIRouter>>();

export const RPCProvider: ParentComponent<{ baseURL: string }> = (props) => {
  const auth = useAuth();
  const rpcClient = createTRPCClient<APIRouter>({
    url: props.baseURL,
    fetch: async (input: RequestInfo | URL | string, init?: RequestInit) => {
      await auth.ready;
      const currentUser = auth.currentUser;
      if (currentUser == null) {
        return fetch(input, init);
      }
      const idToken = await currentUser.getIdToken();
      return fetch(input, {
        ...init,
        headers: cloneHeaders(init?.headers, [
          "authorization",
          `Bearer ${idToken}`,
        ]),
      });
    },
  });

  return (
    <rpcContext.Provider value={rpcClient}>
      {props.children}
    </rpcContext.Provider>
  );
};

const cloneHeaders = (
  h?: HeadersInit,
  ...extras: [string, string][]
): Headers =>
  new Headers([
    ...(h == null ? [] : Array.isArray(h) ? h : Object.entries(h)),
    ...extras,
  ]);

type CreatedResource<T> = ReturnType<typeof createResource<T>>;
export const createQuery = <Q extends keyof Queries>(
  path: Q,
  input: inferProcedureInput<Queries[Q]>
): CreatedResource<inferProcedureOutput<Queries[Q]>> => {
  console.debug("creating query", typeof input);
  const [resource, { refetch, mutate }] = createResource(async () => {
    const rpcClient = useRPC();
    // @ts-ignore
    const result = await rpcClient.query(path, input);
    return result;
  });
  // @ts-ignore
  return [resource, { refetch, mutate }];
};
export const createLiveQuery = <Q extends keyof Queries>(
  path: Q,
  input: () => inferProcedureInput<Queries[Q]>
): CreatedResource<inferProcedureOutput<Queries[Q]>> => {
  const rpcClient = useRPC();
  console.debug("creating query", typeof input);
  const [resource, { refetch, mutate }] = createResource(
    input,
    async (input) => {
      // @ts-ignore
      const result = await rpcClient.query(path, input);
      return result;
    }
  );
  // @ts-ignore
  return [resource, { refetch, mutate }];
};

export const useRPC = () => {
  const rpc = useContext(rpcContext);
  if (rpc == null) {
    throw new Error("not in RPC context");
  }
  return rpc;
};
export const createMutation = <M extends keyof Mutations>(
  path: M,
  input: inferProcedureInput<Mutations[M]>
): Resource<inferProcedureOutput<Mutations[M]>> => {
  // @ts-ignore
  const [resource] = createResource(async () => useRPC().mutation(path, input));
  // @ts-ignore
  return resource;
};
